/**
 * Theme & density management.
 *
 * Three-state theme: light · dark · system.
 * Two-state density: comfortable · compact.
 *
 * Persisted in localStorage. Mirrored to <html> via data-theme / data-density.
 */

import { useEffect, useState } from "react";

type ThemeMode = "light" | "dark" | "system";
type Density = "comfortable" | "compact";

const THEME_KEY = "bizop-theme";
const DENSITY_KEY = "bizop-density";

function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") {
    return typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return mode;
}

function applyTheme(mode: ThemeMode) {
  const resolved = resolveTheme(mode);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") return "system";
    return (localStorage.getItem(THEME_KEY) as ThemeMode) || "system";
  });

  useEffect(() => {
    applyTheme(mode);
    localStorage.setItem(THEME_KEY, mode);
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  return { mode, setMode };
}

export function useDensity() {
  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === "undefined") return "comfortable";
    return (localStorage.getItem(DENSITY_KEY) as Density) || "comfortable";
  });

  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  return { density, setDensity };
}

/**
 * Reactive read of `prefers-reduced-motion: reduce`.
 *
 * Returns `true` when the user has expressed a preference for reduced
 * motion in their OS / browser settings. Components that animate should
 * collapse durations to `0ms` (or skip the animation entirely) when this
 * is `true`. Updates live as the media query changes.
 *
 * SSR-safe — returns `false` when `window` is unavailable.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Sync once in case the value changed between mount and effect.
    setReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return reduced;
}

/** Pre-paint script content. Inject into <head> via index.html to avoid FOUC. */
export const THEME_INIT_SCRIPT = `
(function () {
  try {
    var t = localStorage.getItem('${THEME_KEY}') || 'system';
    var resolved = t === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : t;
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
    var d = localStorage.getItem('${DENSITY_KEY}') || 'comfortable';
    document.documentElement.dataset.density = d;
  } catch (e) {}
})();
`.trim();
